// src/components/GridView/ImportFromExcel/ImportFromExcel.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import type { StylesConfig } from 'react-select';
import { apiService } from '@/services/api';
import { dashboardGroupsService } from '@/components/DashboardGroups/service';
import { getSideMenuLocal } from '@/components/utils/CorgeeHelper';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import type { SideMenu } from '@/types/dashboard';
import './ImportFromExcel.css';

export interface DashboardGroupLike {
  DashboardGroupId: number;
  IsDefault: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Step = 'file' | 'layers' | 'summary' | 'progress' | 'done';

interface ParsedRow {
  title: string;
  cardType: string;
  hasLink: boolean;
  [key: string]: any;
}

// Lightweight local types for custom-field templates (avoids heavy imports)
interface CFOption {
  Id: number;
  Text: string;
  Value: string;
}
interface CFItem {
  CustomFieldId: number;
  Name: string;
  FieldTypeId: number;
  CustomFieldValueOptions?: CFOption[];
}
interface CFLayer {
  LayerId: number;
  Name: string;
  CustomFields: CFItem[];
}

interface CardTypeInfo {
  cardTypeName: string;
  configValueId: number;
  icon: string;
  rows: ParsedRow[];
  emptyFields: CFLayer[]; // field templates — fetched once per card type
}

interface Layer {
  LayerId: number;
  Name: string;
  Color?: string;
  TextColor?: string;
  IsDefault?: boolean;
}

interface LayerOption {
  value: number;
  label: string;
  layer: Layer;
}

interface ProgressItem {
  title: string;
  cardType: string;
  icon: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
}

export interface ImportFromExcelProps {
  open: boolean;
  onClose: () => void;
  dashboardId: number;
  dashboardGroups: DashboardGroupLike[];
  configValueId: number;
  projectId?: string;
  onImportComplete?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function parseExcel(file: File): Promise<{ rows: ParsedRow[]; error?: string }>
{
  return new Promise(resolve =>
  {
    const reader = new FileReader();

    reader.onload = e =>
    {
      try
      {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0]!;
        const sheet = workbook.Sheets[sheetName]!;

        // Fix date serial numbers
        Object.values(sheet).forEach((cell: any) =>
        {
          if (
            cell?.t === 'n' &&
            cell?.v > 10000 &&
            typeof cell?.w === 'string' &&
            cell.w.includes('/')
          )
          {
            const d = new Date(Math.round((cell.v - 25569) * 86400 * 1000));
            cell.w = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
          }
        });

        const allRows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 }) as any[][];

        if (allRows.length < 2)
        {
          resolve({ rows: [], error: 'No valid data was found in this file.' });
          return;
        }

        // ── Auto-detect format: 2-header (layer row + headers) or 1-header ──
        // The header row is the one that contains "Card Type" or "Link"
        const rowContains = (row: any[], ...vals: string[]) =>
          row.some(c => vals.includes(String(c ?? '').trim()));

        let layerRowRaw: any[] = [];
        let headerRow: any[] = [];
        let dataStartIdx: number;

        if (rowContains(allRows[1] ?? [], 'Card Type', 'Link'))
        {
          // Standard 2-row format exported by the GridView
          layerRowRaw = allRows[0] ?? [];
          headerRow = allRows[1] ?? [];
          dataStartIdx = 2;
        }
        else if (rowContains(allRows[0] ?? [], 'Card Type', 'Link'))
        {
          // Simple 1-row header format (no layer row)
          layerRowRaw = [];
          headerRow = allRows[0] ?? [];
          dataStartIdx = 1;
        }
        else
        {
          // Unknown format — try row 1 anyway
          layerRowRaw = allRows[0] ?? [];
          headerRow = allRows[1] ?? [];
          dataStartIdx = 2;
        }

        const dataRows = allRows
          .slice(dataStartIdx)
          .filter(r => r.some((c: any) => c !== undefined && c !== null && c !== ''));

        if (dataRows.length === 0)
        {
          resolve({ rows: [], error: 'No data rows were found in this file.' });
          return;
        }

        // ── Build column metadata with layer propagation ──────────────────────
        // XLSX returns undefined for merged cells beyond the first — propagate forward
        let currentLayer = '';
        const columnMeta = headerRow.map((header: any, i: number) =>
        {
          const h = String(header ?? '').trim();
          const rawLayer = String(layerRowRaw[i] ?? '').trim();

          // Update propagated layer when a new value appears
          if (rawLayer) currentLayer = rawLayer;

          // Only apply layer prefix to actual field columns (not title, Card Type, Link)
          const isFixedCol = i === 0 || h === 'Card Type' || h === 'Link' || !h;
          const layer = isFixedCol ? '' : currentLayer;

          return { header: h, layer };
        });

        const titleHeader = columnMeta[0]?.header || '';

        // ── Parse data rows ───────────────────────────────────────────────────
        const rows: ParsedRow[] = [];
        let skippedLink = 0;
        let skippedNoTitle = 0;
        let skippedNoCardType = 0;

        for (const dataRow of dataRows)
        {
          const row: any = {};

          for (let i = 0; i < columnMeta.length; i++)
          {
            const { header, layer } = columnMeta[i]!;
            if (!header) continue;

            const cell = dataRow[i];
            const value = cell !== undefined && cell !== null && cell !== '' ? cell : undefined;
            if (value === undefined) continue;

            if (header === 'Link')
            {
              row.__link = value;
              continue;
            }

            const key = layer ? `${layer} : ${header}` : header;
            row[key] = value;
          }

          const title = String(row[titleHeader] ?? '').trim();
          const cardType = String(row['Card Type'] ?? '').trim();

          if (row.__link)
          {
            skippedLink++;
            continue;
          }
          if (!title)
          {
            skippedNoTitle++;
            continue;
          }
          if (!cardType)
          {
            skippedNoCardType++;
            continue;
          }

          rows.push({ ...row, title, cardType, hasLink: false });
        }

        if (rows.length === 0)
        {
          let reason: string;
          if (skippedLink > 0 && skippedNoTitle + skippedNoCardType === 0)
          {
            reason = `All ${skippedLink} row(s) already have a Link (they are existing cards). Add new rows without a Link value to import new cards.`;
          }
          else if (skippedNoCardType > 0)
          {
            reason = `${skippedNoCardType} row(s) are missing a "Card Type" value. Fill in the "Card Type" column for each new row.`;
          }
          else if (skippedNoTitle > 0)
          {
            reason = `${skippedNoTitle} row(s) are missing a title. Fill in the first column for each new row.`;
          }
          else
          {
            reason =
              'No new data was found. New rows must have a title and a "Card Type" filled in, with no Link value.';
          }
          resolve({ rows: [], error: reason });
        }
        else
        {
          resolve({ rows });
        }
      }
      catch (err)
      {
        console.error('[ImportFromExcel] Parse error:', err);
        resolve({ rows: [], error: 'An error occurred trying to import this file.' });
      }
    };

    reader.onerror = () => resolve({ rows: [], error: 'Failed to read file.' });
    reader.readAsBinaryString(file);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Custom-field helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Parse a date string (M/D/YYYY, YYYY-MM-DD, or JS-parseable) into a Date. */
function parseDate(raw: any): Date | null
{
  if (raw == null) return null;
  const str = String(raw).trim();
  if (!str) return null;

  // DD/MM/YYYY or DD-MM-YYYY — ambiguous; if day > 12 it's definitely D/M
  const parts = str.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (parts)
  {
    const a = parseInt(parts[1]!),
      b = parseInt(parts[2]!),
      y = parseInt(parts[3]!);
    if (a > 12)
    {
      // Must be DD/MM/YYYY
      const d = new Date(y, b - 1, a);
      if (!isNaN(d.getTime())) return d;
    }
    // Prefer M/D/YYYY (US — output of our Excel date normalisation)
    const us = new Date(y, a - 1, b);
    if (!isNaN(us.getTime())) return us;
    const eu = new Date(y, b - 1, a);
    if (!isNaN(eu.getTime())) return eu;
  }

  // ISO, RFC and everything else the engine can parse
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

/** Validate and convert a raw Excel cell value to the API payload for a field. */
function coerceFieldValue(
  field: CFItem,
  rawValue: any,
): { value: any; text: string; valid: boolean; reason?: string }
{
  const str = String(rawValue ?? '').trim();
  if (!str) return { value: null, text: '', valid: false, reason: 'empty' };

  switch (field.FieldTypeId)
  {
    // 1 = text, 5 = multiline, 11 = hyperlink
    case 1:
    case 5:
    case 11:
      return { value: str, text: str, valid: true };

    // 2 = integer, 4 = integer alt
    case 2:
    case 4: {
      const n = parseInt(str.replace(/[^\d\-]/g, ''), 10);
      if (isNaN(n))
        return { value: null, text: '', valid: false, reason: `"${str}" is not a valid integer` };
      return { value: n, text: String(n), valid: true };
    }

    // 3 = decimal
    case 3: {
      // Accept comma or dot as decimal separator
      const n = parseFloat(str.replace(',', '.').replace(/[^\d.\-]/g, ''));
      if (isNaN(n))
        return { value: null, text: '', valid: false, reason: `"${str}" is not a valid number` };
      return { value: n, text: String(n), valid: true };
    }

    // 6 = date
    case 6: {
      const d = parseDate(str);
      if (!d)
        return { value: null, text: '', valid: false, reason: `"${str}" is not a valid date` };
      const formatted = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
      return { value: formatted, text: formatted, valid: true };
    }

    // 7 = multi-select
    case 7: {
      const opts = field.CustomFieldValueOptions;
      if (!opts?.length)
        return { value: null, text: '', valid: false, reason: 'no options available' };
      const parts = str
        .split(/[,;]/)
        .map(s => s.trim())
        .filter(Boolean);
      const matched = parts
        .map(p => opts.find(o => o.Text.toLowerCase() === p.toLowerCase()))
        .filter((o): o is CFOption => !!o);
      if (!matched.length)
        return { value: null, text: '', valid: false, reason: `"${str}" — no matching options` };
      return {
        value: matched.map(o => o.Value).join(','),
        text: matched.map(o => o.Text).join(', '),
        valid: true,
      };
    }

    // 8 = combo alt, 9 = combo
    case 8:
    case 9: {
      const opts = field.CustomFieldValueOptions;
      if (!opts?.length)
        return { value: null, text: '', valid: false, reason: 'no options available' };
      const opt = opts.find(o => o.Text.toLowerCase() === str.toLowerCase());
      if (!opt)
        return { value: null, text: '', valid: false, reason: `"${str}" not found in options` };
      return { value: opt.Value, text: opt.Text, valid: true };
    }

    default:
      return { value: str, text: str, valid: true };
  }
}

/**
 * Build CustomFields for the POST /conversation/ create payload.
 * Format: [{ CustomFieldId, Value, CoreTaskID: 0, FieldTypeId }]
 * Only includes fields that have a matching value in the Excel row.
 */
function buildConversationCustomFields(row: ParsedRow, emptyFields: CFLayer[]): any[]
{
  const result: any[] = [];
  for (const layer of emptyFields)
  {
    for (const field of layer.CustomFields)
    {
      const keyFull = `${layer.Name} : ${field.Name}`;
      const keyShort = field.Name;
      const rawValue = row[keyFull] !== undefined ? row[keyFull] : row[keyShort];
      if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') continue;

      const { value, valid } = coerceFieldValue(field, rawValue);
      if (!valid || value === null) continue;

      result.push({
        CustomFieldId: field.CustomFieldId,
        Value: value,
        CoreTaskID: 0,
        FieldTypeId: field.FieldTypeId,
      });
    }
  }
  return result;
}

/**
 * Build the payload array for POST api/CustomFieldValueMultiple/.
 * Matches each ParsedRow field key ("{LayerName} : {FieldName}" or "{FieldName}")
 * against the empty-fields template and coerces the value.
 */
function buildFieldPayloads(
  row: ParsedRow,
  emptyFields: CFLayer[],
  coreTaskId: number,
  socketConnectionId: string | null,
): any[]
{
  const payloads: any[] = [];
  for (const layer of emptyFields)
  {
    for (const field of layer.CustomFields)
    {
      const keyFull = `${layer.Name} : ${field.Name}`;
      const keyShort = field.Name;
      const rawValue = row[keyFull] !== undefined ? row[keyFull] : row[keyShort];
      if (rawValue === undefined || rawValue === null || String(rawValue).trim() === '') continue;

      const { value, text, valid } = coerceFieldValue(field, rawValue);
      if (!valid || value === null) continue;

      payloads.push({
        CustomFieldId: field.CustomFieldId,
        CoreTaskId: coreTaskId,
        ConversationId: null,
        Value: value,
        SelectedValuesText: text,
        LayerId: layer.LayerId,
        SocketConnectionId: socketConnectionId ?? null,
      });
    }
  }
  return payloads;
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary helpers
// ─────────────────────────────────────────────────────────────────────────────

interface RowLayerPreview {
  layerName: string;
  fieldNames: string[];
}

function getRowFieldsPreview(
  row: ParsedRow,
  emptyFields: CFLayer[],
  selectedLayerNames: string[],
): RowLayerPreview[]
{
  const result: RowLayerPreview[] = [];
  for (const layer of emptyFields)
  {
    if (selectedLayerNames.length > 0 && !selectedLayerNames.includes(layer.Name)) continue;
    const fieldNames: string[] = [];
    for (const field of layer.CustomFields)
    {
      const keyFull = `${layer.Name} : ${field.Name}`;
      const keyShort = field.Name;
      const raw = row[keyFull] !== undefined ? row[keyFull] : row[keyShort];
      if (raw === undefined || raw === null || String(raw).trim() === '') continue;
      const { valid } = coerceFieldValue(field, raw);
      if (valid) fieldNames.push(field.Name);
    }
    if (fieldNames.length > 0) result.push({ layerName: layer.Name, fieldNames });
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────

const INITIAL_POS = { x: Math.max(0, (window.innerWidth - 520) / 2), y: 80 };

const layerSelectStyles: StylesConfig<LayerOption, true> = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    borderRadius: '2px',
    boxShadow: 'none',
    fontSize: '12px',
    borderColor: state.isFocused ? '#90caf9' : '#ccc',
    '&:hover': { borderColor: '#90caf9' },
  }),
  valueContainer: base => ({ ...base, padding: '2px 6px' }),
  input: base => ({ ...base, margin: 0, padding: 0 }),
  indicatorSeparator: base => ({ ...base, backgroundColor: '#ddd' }),
  dropdownIndicator: base => ({
    ...base,
    padding: '0 6px',
    color: '#999',
    '&:hover': { color: '#666' },
  }),
  clearIndicator: base => ({
    ...base,
    padding: '0 4px',
    color: '#999',
    '&:hover': { color: '#666' },
  }),
  placeholder: base => ({ ...base, fontSize: '12px', color: '#aaa' }),
  multiValue: base => ({
    ...base,
    backgroundColor: '#fff',
    borderRadius: '2px',
    border: '1px solid #ddd',
    fontSize: '12px',
  }),
  multiValueLabel: base => ({ ...base, color: '#555', padding: '1px 5px', fontSize: '12px' }),
  multiValueRemove: base => ({
    ...base,
    color: '#999',
    borderRight: '1px solid #ddd',
    paddingLeft: '4px',
    paddingRight: '4px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#f5f5f5', color: '#333' },
  }),
  menu: base => ({ ...base, marginTop: 0, borderRadius: 0, maxHeight: '180px', zIndex: 9999 }),
  menuList: base => ({ ...base, padding: 0, maxHeight: '178px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    padding: '4px 10px',
    backgroundColor: state.isFocused ? '#62accb' : state.isSelected ? '#f5faff' : '#fff',
    color: state.isFocused ? '#fff' : '#444',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#62accb' },
  }),
};

export const ImportFromExcel: React.FC<ImportFromExcelProps> = ({
  open,
  onClose,
  dashboardId,
  dashboardGroups,
  configValueId: _configValueId,
  projectId,
  onImportComplete,
}) =>
{
  const socketConnectionId = useSocketConnectionId();

  // ── Steps ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('file');

  // ── File / parse ──────────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState('');
  const [detectedCardTypes, setDetectedCardTypes] = useState<CardTypeInfo[]>([]);

  // ── Layer selection ────────────────────────────────────────────────────────
  const [layersByType, setLayersByType] = useState<Record<string, Layer[]>>({});
  const [selectedLayers, setSelectedLayers] = useState<Record<string, Layer[]>>({});
  const [layersLoading, setLayersLoading] = useState(false);

  // ── Progress ───────────────────────────────────────────────────────────────
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
  const [progressCount, setProgressCount] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const cancelRef = useRef(false);

  // ── Window state ───────────────────────────────────────────────────────────
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);

  // ── Dragging ───────────────────────────────────────────────────────────────
  const [position, setPosition] = useState(INITIAL_POS);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const progressListRef = useRef<HTMLDivElement>(null);

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() =>
  {
    if (open)
    {
      setStep('file');
      setSelectedFile(null);
      setParseError('');
      setDetectedCardTypes([]);
      setLayersByType({});
      setSelectedLayers({});
      setProgressItems([]);
      setProgressCount(0);
      setIsImporting(false);
      setMinimized(false);
      setMaximized(false);
      setPosition(INITIAL_POS);
      cancelRef.current = false;
    }
  }, [open]);

  // ── beforeunload warning ───────────────────────────────────────────────────
  useEffect(() =>
  {
    if (!isImporting) return;
    const handler = (e: BeforeUnloadEvent) =>
    {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isImporting]);

  // ── Drag ───────────────────────────────────────────────────────────────────
  const handleDragStart = useCallback(
    (e: React.MouseEvent) =>
    {
      if (maximized) return;
      e.preventDefault();
      setIsDragging(true);
      dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    },
    [maximized, position],
  );

  useEffect(() =>
  {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) =>
    {
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOffset.current.y)),
      });
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () =>
    {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // ── Minimize / Maximize ────────────────────────────────────────────────────
  const handleMinimize = useCallback(() =>
  {
    if (minimized)
    {
      setMinimized(false);
      setPosition(INITIAL_POS);
    }
    else
    {
      setMinimized(true);
      setMaximized(false);
      setPosition({
        x: Math.max(8, window.innerWidth - 420),
        y: window.innerHeight - 48,
      });
    }
  }, [minimized]);

  const handleMaximize = useCallback(() =>
  {
    if (maximized)
    {
      setMaximized(false);
      setMinimized(false);
      setPosition(INITIAL_POS);
    }
    else
    {
      setMaximized(true);
      setMinimized(false);
    }
  }, [maximized]);

  // ── Close ──────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() =>
  {
    if (isImporting)
    {
      if (
        window.confirm(
          'The import is still in progress. If you close, the remaining cards will not be imported. Are you sure?',
        )
      )
      {
        cancelRef.current = true;
        setIsImporting(false);
        onClose();
      }
    }
    else
    {
      onClose();
    }
  }, [isImporting, onClose]);

  // ── File change ────────────────────────────────────────────────────────────
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setParseError('');
    setDetectedCardTypes([]);
  }, []);

  // ── Step 1 → 2: parse + detect types ──────────────────────────────────────
  const handleParseAndNext = useCallback(async () =>
  {
    if (!selectedFile) return;

    const { rows, error } = await parseExcel(selectedFile);
    if (error || rows.length === 0)
    {
      setParseError(error || 'No valid data found.');
      return;
    }

    // Detect unique Card Types and map to configValueId via sideMenuItems
    const sideMenuItems: SideMenu[] = getSideMenuLocal() ?? [];
    const uniqueTypes = [...new Set(rows.map(r => r.cardType))].filter(Boolean);

    const cardTypes: CardTypeInfo[] = [];
    for (const typeName of uniqueTypes)
    {
      const menuItem = sideMenuItems.find(m => m.Text === typeName || m.Name === typeName);
      const cid = menuItem?.ConfigValueID ?? 0;
      cardTypes.push({
        cardTypeName: typeName,
        configValueId: cid,
        icon: menuItem?.Icon ?? '',
        rows: rows.filter(r => r.cardType === typeName),
        emptyFields: [],
      });
    }

    // Extract layer names referenced in the Excel column keys ("LayerName : FieldName")
    const excelLayerNames = new Set<string>();
    for (const row of rows)
    {
      for (const key of Object.keys(row))
      {
        const sep = key.indexOf(' : ');
        if (sep > 0) excelLayerNames.add(key.substring(0, sep));
      }
    }

    // Fetch layers AND empty custom-field templates for each card type
    setLayersLoading(true);
    const layersMap: Record<string, Layer[]> = {};
    const defaultSelections: Record<string, Layer[]> = {};
    const emptyFieldsMap: Record<string, CFLayer[]> = {};

    await Promise.all(
      cardTypes.map(async ct =>
      {
        if (ct.configValueId > 0)
        {
          const pid = projectId ? parseInt(projectId) : undefined;
          const [layers, fields] = await Promise.all([
            dashboardGroupsService.getLayersByConfigValue(ct.configValueId),
            dashboardGroupsService.getEmptyCustomFields(ct.configValueId, pid),
          ]);
          layersMap[ct.cardTypeName] = layers || [];
          const matched = (layers || []).filter(l => excelLayerNames.has(l.Name));
          defaultSelections[ct.cardTypeName] =
            matched.length > 0 ? matched : layers?.[0] ? [layers[0]] : [];

          const cfLayers = (fields as CFLayer[]) || [];

          // ── Fetch dynamic options for fields with a ServiceUrl ─────────────
          // GetEmptyCustomFieldsByConfigValueId returns combo/multi-select/person
          // fields with a ServiceUrl instead of inline CustomFieldValueOptions.
          // We resolve them here so coerceFieldValue can do text→value matching.
          await Promise.all(
            cfLayers.flatMap(layer =>
              layer.CustomFields.filter(f =>
              {
                const anyF = f as any;
                return (
                  [7, 8, 9].includes(f.FieldTypeId) &&
                  anyF.ServiceUrl &&
                  !f.CustomFieldValueOptions?.length
                );
              }).map(async field =>
              {
                try
                {
                  const anyF = field as any;
                  // Strip leading "api/" since apiService already includes it
                  let url = String(anyF.ServiceUrl).replace(/^api\//i, '');
                  // Replace configValueId=null/0 placeholder with the actual card-type configValueId
                  if (ct.configValueId > 0)
                  {
                    if (/configValueId=(null|0)(&|$)/i.test(url))
                    {
                      url = url.replace(
                        /configValueId=(null|0)/i,
                        `configValueId=${ct.configValueId}`,
                      );
                    }
                    else if (!url.includes('configValueId='))
                    {
                      url += (url.includes('?') ? '&' : '?') + `configValueId=${ct.configValueId}`;
                    }
                  }
                  // Append projectId when available and not already present in the URL
                  if (pid && !url.includes('projectId='))
                  {
                    url += (url.includes('?') ? '&' : '?') + `projectId=${pid}`;
                  }
                  // Append layerId from the parent layer when not already present
                  // Use != null (not truthy) so layerId=0 (transparent layer) is also included
                  if (layer.LayerId != null && !url.includes('layerId='))
                  {
                    url += `&layerId=${layer.LayerId}`;
                  }
                  const data = await apiService.get<any[]>(url);
                  const textKey = anyF.TextField || 'Name';
                  const valueKey = anyF.ValueField || 'Id';
                  field.CustomFieldValueOptions = (data || []).map((item, i) => ({
                    Id: item.Id ?? item[valueKey] ?? i,
                    Text: String(item[textKey] ?? ''),
                    Value: String(item[valueKey] ?? ''),
                  }));
                }
                catch (e)
                {
                  console.warn('[ImportFromExcel] Options fetch failed for field:', field.Name, e);
                }
              }),
            ),
          );

          emptyFieldsMap[ct.cardTypeName] = cfLayers;
        }
        else
        {
          layersMap[ct.cardTypeName] = [];
          defaultSelections[ct.cardTypeName] = [];
          emptyFieldsMap[ct.cardTypeName] = [];
        }
      }),
    );

    // Attach empty fields to each card type info
    const cardTypesWithFields = cardTypes.map(ct => ({
      ...ct,
      emptyFields: emptyFieldsMap[ct.cardTypeName] ?? [],
    }));

    setDetectedCardTypes(cardTypesWithFields);
    setLayersByType(layersMap);
    setSelectedLayers(defaultSelections);
    setLayersLoading(false);
    setStep('layers');
  }, [selectedFile]);

  // ── Step 2 → 3: summary ───────────────────────────────────────────────────
  const handleGoToSummary = useCallback(() =>
  {
    setStep('summary');
  }, []);

  // ── Step 3 → 4: import ────────────────────────────────────────────────────
  const handleStartImport = useCallback(async () =>
  {
    // Build initial progress items
    const items: ProgressItem[] = detectedCardTypes.flatMap(ct =>
      ct.rows.map(row => ({
        title: row.title,
        cardType: ct.cardTypeName,
        icon: ct.icon,
        status: 'pending' as const,
      })),
    );
    setProgressItems(items);
    setProgressCount(0);
    setStep('progress');
    setIsImporting(true);
    cancelRef.current = false;

    // Determine target group
    const nonDefault = dashboardGroups.find(g => !g.IsDefault);
    const targetGroupId = nonDefault?.DashboardGroupId || dashboardGroups[0]?.DashboardGroupId || 0;

    let current = 0;

    for (const cardType of detectedCardTypes)
    {
      if (cancelRef.current) break;

      const selectedLayerArr = selectedLayers[cardType.cardTypeName] ?? [];

      for (const row of cardType.rows)
      {
        if (cancelRef.current) break;

        const conversationCustomFields = buildConversationCustomFields(row, cardType.emptyFields);

        const payload = {
          ConversationId: 0,
          Attachments: [],
          IsFromProject: !!(projectId && parseInt(projectId) > 0),
          ConfigValueId: cardType.configValueId,
          ConversationLayers: selectedLayerArr.map(l => ({
            LayerId: l.LayerId,
            Name: l.Name,
            Color: l.Color || '',
            TextColor: l.TextColor || '',
            IsEnabled: false,
            IsDefault: false,
          })),
          ConversationGroups: [],
          ConversationSecurityGroups: [],
          CustomFields: conversationCustomFields,
          Title: row.title,
          Body: '',
          ProjectId: projectId ? parseInt(projectId) : null,
          ProjectName: '',
          RelatedConversationId: 0,
          RelatedReplyId: 0,
          IsRedFlag: false,
          IsFavorite: false,
          SideMenuText: '',
          Icon: cardType.icon,
          Tags: [],
          Hashtags: [],
          IsEnable: true,
          IsAdmin: true,
          IsAdminProject: false,
          OwnedById: 0,
          DashboardId: dashboardId,
          DashboardGroupId: targetGroupId,
          Metadata: {},
          SocketConnectionId: socketConnectionId,
        };

        const idx = current;
        try
        {
          const response = await dashboardGroupsService.createConversation(payload);

          if (response?.Conversation?.ConversationId)
          {
            const conv = response.Conversation;
            const coreTaskId: number = conv.CoreTaskId ?? conv.ConversationId;

            await dashboardGroupsService.insertDashboardGroupConversation({
              DashboardId: dashboardId,
              DashboardGroupId: targetGroupId,
              ConversationId: conv.ConversationId,
              ParentConversationId: 0,
              PreviousParentId: null,
              socketConnectionId: socketConnectionId || '',
            });

            // ── Save custom field values from the Excel row ─────────────────
            const cfPayloads = buildFieldPayloads(
              row,
              cardType.emptyFields,
              coreTaskId,
              socketConnectionId ?? null,
            );
            if (cfPayloads.length > 0)
            {
              try
              {
                await apiService.post('CustomFieldValueMultiple/', {
                  customFields: cfPayloads,
                  types: cfPayloads.map(() => 1), // 1 = new (POST)
                });
              }
              catch (cfErr)
              {
                console.warn('[ImportFromExcel] Custom fields not saved for', row.title, cfErr);
              }
            }

            setProgressItems(prev =>
              prev.map((item, i) => (i === idx ? { ...item, status: 'success' } : item)),
            );
          }
          else
          {
            setProgressItems(prev =>
              prev.map((item, i) =>
                i === idx ? { ...item, status: 'error', message: 'Failed to create card' } : item,
              ),
            );
          }
        }
        catch (err: any)
        {
          setProgressItems(prev =>
            prev.map((item, i) =>
              i === idx
                ? { ...item, status: 'error', message: err?.message || 'Unknown error' }
                : item,
            ),
          );
        }

        current++;
        setProgressCount(current);

        // Scroll progress list to bottom
        if (progressListRef.current)
        {
          progressListRef.current.scrollTop = progressListRef.current.scrollHeight;
        }
      }
    }

    setIsImporting(false);
    if (!cancelRef.current)
    {
      setStep('done');
      onImportComplete?.();
    }
  }, [
    detectedCardTypes,
    selectedLayers,
    dashboardGroups,
    dashboardId,
    projectId,
    socketConnectionId,
    onImportComplete,
  ]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!open) return null;

  const totalCards = detectedCardTypes.reduce((s, ct) => s + ct.rows.length, 0);
  const succeeded = progressItems.filter(i => i.status === 'success').length;
  const failed = progressItems.filter(i => i.status === 'error').length;

  const modalStyle: React.CSSProperties = maximized
    ? { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', borderRadius: 0 }
    : minimized
      ? { position: 'fixed', top: position.y, left: position.x, width: 260 }
      : { position: 'fixed', top: position.y, left: position.x, width: 520, maxHeight: '90vh' };

  const modal = (
    <div className="ife-overlay">
      <div className={`ife-modal${minimized ? ' ife-modal--minimized' : ''}`} style={modalStyle}>
        {/* ── Title bar ──────────────────────────────────────────────────── */}
        <div
          className="ife-title-bar"
          onMouseDown={handleDragStart}
          style={{ cursor: maximized ? 'default' : isDragging ? 'grabbing' : 'grab' }}
        >
          <span className="ife-title">
            Import From Excel
            {minimized && step === 'progress' && (
              <span className="ife-title-badge ife-title-badge--progress">
                {progressCount} of {totalCards}
              </span>
            )}
            {minimized && step === 'done' && (
              <span className="ife-title-badge ife-title-badge--done">
                <i
                  className="material-icons"
                  style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 2 }}
                >
                  check_circle
                </i>
                Complete
              </span>
            )}
          </span>
          <div className="ife-win-controls">
            <button
              className="ife-win-btn"
              title={minimized ? 'Restore' : 'Minimize'}
              onClick={handleMinimize}
            >
              <span className="ife-win-icon">{minimized ? '▲' : '─'}</span>
            </button>
            <button
              className="ife-win-btn"
              title={maximized ? 'Restore' : 'Maximize'}
              onClick={handleMaximize}
            >
              <span className="ife-win-icon">{maximized ? '❐' : '□'}</span>
            </button>
            <button className="ife-win-btn ife-win-btn--close" title="Close" onClick={handleClose}>
              <span className="ife-win-icon">✕</span>
            </button>
          </div>
        </div>

        {/* ── Body (hidden when minimized) ───────────────────────────────── */}
        {!minimized && (
          <div className="ife-body">
            {/* ── STEP: file ──────────────────────────────────────────────── */}
            {step === 'file' && (
              <div className="ife-step">
                <div className="ife-step-label">
                  <span className="ife-step-num">Step 1:</span> Choose a file to import (Excel
                  File).
                </div>

                <div className="ife-file-row">
                  <label className="ife-file-btn" htmlFor="ife-file-input">
                    Seleccionar archivo
                  </label>
                  <input
                    id="ife-file-input"
                    type="file"
                    accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="ife-file-hidden"
                    onChange={handleFileChange}
                  />
                  {selectedFile && <span className="ife-file-name">{selectedFile.name}</span>}
                </div>

                {parseError && <div className="ife-error">{parseError}</div>}

                <div className="ife-note">
                  <strong>Note:</strong> To import new cards into this dashboard first export a
                  spreadsheet, then use it as a template to fill out and import back in. Only new
                  cards will be added — existing cards will not be updated.
                </div>

                <div className="ife-footer">
                  <button className="ife-btn ife-btn--secondary" onClick={handleClose}>
                    Cancel
                  </button>
                  <button
                    className="ife-btn ife-btn--primary"
                    disabled={!selectedFile}
                    onClick={handleParseAndNext}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP: layers ────────────────────────────────────────────── */}
            {step === 'layers' && (
              <div className="ife-step">
                {layersLoading ? (
                  <div className="ife-loading">Loading layers…</div>
                ) : (
                  <>
                    <div className="ife-step-label">
                      <span className="ife-step-num">Step 2:</span> Select layers where this
                      information will be imported.
                    </div>

                    {detectedCardTypes.map(ct => (
                      <div key={ct.cardTypeName} className="ife-layer-group">
                        <div className="ife-layer-type-label">
                          {ct.icon && (
                            <i className={ct.icon} style={{ fontSize: 13, marginRight: 4 }} />
                          )}
                          <strong>Cards Type ({ct.cardTypeName}):</strong>
                          <span className="ife-layer-count">{ct.rows.length} card(s)</span>
                        </div>

                        <div className="ife-layer-select-row">
                          <label className="ife-layer-label">Layers</label>
                          <div style={{ flex: 1 }}>
                            <Select<LayerOption, true>
                              isMulti
                              value={(selectedLayers[ct.cardTypeName] ?? []).map(l => ({
                                value: l.LayerId,
                                label: l.Name,
                                layer: l,
                              }))}
                              onChange={newValues =>
                              {
                                const layers = Array.from(newValues).map(v => v.layer);
                                setSelectedLayers(prev => ({
                                  ...prev,
                                  [ct.cardTypeName]: layers,
                                }));
                              }}
                              options={(layersByType[ct.cardTypeName] ?? []).map(l => ({
                                value: l.LayerId,
                                label: l.Name,
                                layer: l,
                              }))}
                              styles={layerSelectStyles}
                              placeholder="— No layer —"
                              isClearable
                              isSearchable
                              closeMenuOnSelect={false}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              classNamePrefix="ife-rs"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="ife-note">
                      <strong>Note:</strong> To import new cards into this dashboard first export a
                      spreadsheet, then use it as a template to fill out and import back in. Only
                      new cards will be added — existing cards will not be updated.
                    </div>

                    <div className="ife-footer">
                      <button
                        className="ife-btn ife-btn--secondary"
                        onClick={() => setStep('file')}
                      >
                        Back
                      </button>
                      <button className="ife-btn ife-btn--secondary" onClick={handleClose}>
                        Cancel
                      </button>
                      <button className="ife-btn ife-btn--primary" onClick={handleGoToSummary}>
                        Import
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── STEP: summary ───────────────────────────────────────────── */}
            {step === 'summary' && (
              <div className="ife-step">
                <div className="ife-summary-title">Summary</div>
                <div className="ife-summary-count">
                  <strong>{totalCards}</strong> card(s) to be imported:
                </div>

                <div className="ife-summary-list">
                  {detectedCardTypes.map(ct =>
                  {
                    const selectedLayerNames = (selectedLayers[ct.cardTypeName] ?? []).map(
                      l => l.Name,
                    );
                    return (
                      <div key={ct.cardTypeName} className="ife-summary-group">
                        {/* Group header */}
                        <div className="ife-summary-group-header">
                          {ct.icon ? (
                            <i className={ct.icon} style={{ fontSize: 13 }} />
                          ) : (
                            <i className="material-icons" style={{ fontSize: 13 }}>
                              description
                            </i>
                          )}
                          <span>
                            {ct.cardTypeName} — {ct.rows.length} card
                            {ct.rows.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Layer chips */}
                        {selectedLayerNames.length > 0 && (
                          <div className="ife-summary-chips">
                            <span
                              style={{
                                fontSize: 11,
                                color: '#888',
                                marginRight: 2,
                                alignSelf: 'center',
                              }}
                            >
                              Layers:
                            </span>
                            {selectedLayerNames.map(name => (
                              <span key={name} className="ife-summary-chip">
                                {name}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Card list — each card shows its own field chips */}
                        {ct.rows.map((row, i) =>
                        {
                          const rowFields = getRowFieldsPreview(
                            row,
                            ct.emptyFields,
                            selectedLayerNames,
                          );
                          return (
                            <div key={i} className="ife-summary-item ife-summary-item--detailed">
                              <span style={{ color: '#bbb', fontSize: 11, flexShrink: 0 }}>·</span>
                              <div className="ife-summary-item-content">
                                <span className="ife-summary-item-title">{row.title}</span>
                                {rowFields.map(lp => (
                                  <div key={lp.layerName} className="ife-summary-item-fields">
                                    <span className="ife-summary-chip-layer-label">
                                      {lp.layerName}:
                                    </span>
                                    {lp.fieldNames.map(name => (
                                      <span key={name} className="ife-summary-field-chip">
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="ife-footer">
                  <button className="ife-btn ife-btn--secondary" onClick={() => setStep('layers')}>
                    Back
                  </button>
                  <button className="ife-btn ife-btn--secondary" onClick={handleClose}>
                    Cancel
                  </button>
                  <button className="ife-btn ife-btn--primary" onClick={handleStartImport}>
                    OK
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP: progress ──────────────────────────────────────────── */}
            {step === 'progress' && (
              <div className="ife-step">
                <div className="ife-progress-header">
                  <span>Importing cards…</span>
                  <span className="ife-progress-count">
                    {progressCount} / {totalCards}
                  </span>
                </div>

                <div className="ife-progress-bar-track">
                  <div
                    className="ife-progress-bar-fill"
                    style={{ width: `${totalCards > 0 ? (progressCount / totalCards) * 100 : 0}%` }}
                  />
                </div>

                <div className="ife-progress-list" ref={progressListRef}>
                  {progressItems.map((item, i) => (
                    <div key={i} className={`ife-progress-item ife-progress-item--${item.status}`}>
                      <span className="ife-progress-item-icon">
                        {item.status === 'success' && (
                          <i className="material-icons" style={{ color: '#4caf50', fontSize: 14 }}>
                            check_circle
                          </i>
                        )}
                        {item.status === 'error' && (
                          <i className="material-icons" style={{ color: '#f44336', fontSize: 14 }}>
                            error
                          </i>
                        )}
                        {item.status === 'pending' && (
                          <i className="material-icons" style={{ color: '#bbb', fontSize: 14 }}>
                            radio_button_unchecked
                          </i>
                        )}
                      </span>
                      {item.icon ? (
                        <i
                          className={item.icon}
                          style={{ fontSize: 12, color: '#555', marginRight: 4 }}
                        />
                      ) : null}
                      <span className="ife-progress-item-title">{item.title}</span>
                      {item.message && (
                        <span className="ife-progress-item-msg">{item.message}</span>
                      )}
                    </div>
                  ))}
                </div>

                {isImporting && (
                  <div className="ife-note ife-note--warning">
                    Import in progress. Please do not close or refresh the page.
                  </div>
                )}

                {!isImporting && (
                  <div className="ife-footer">
                    <button className="ife-btn ife-btn--secondary" onClick={handleClose}>
                      Close
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP: done ──────────────────────────────────────────────── */}
            {step === 'done' && (
              <div className="ife-step">
                <div className="ife-done">
                  <i className="material-icons ife-done-icon">check_circle</i>
                  <div className="ife-done-title">Import Complete</div>
                  <div className="ife-done-stats">
                    <span className="ife-stat-success">{succeeded} created</span>
                    {failed > 0 && <span className="ife-stat-error">{failed} failed</span>}
                  </div>
                </div>

                <div className="ife-progress-list ife-progress-list--done" ref={progressListRef}>
                  {progressItems.map((item, i) => (
                    <div key={i} className={`ife-progress-item ife-progress-item--${item.status}`}>
                      <span className="ife-progress-item-icon">
                        {item.status === 'success' && (
                          <i className="material-icons" style={{ color: '#4caf50', fontSize: 14 }}>
                            check_circle
                          </i>
                        )}
                        {item.status === 'error' && (
                          <i className="material-icons" style={{ color: '#f44336', fontSize: 14 }}>
                            error
                          </i>
                        )}
                      </span>
                      {item.icon ? (
                        <i
                          className={item.icon}
                          style={{ fontSize: 12, color: '#555', marginRight: 4 }}
                        />
                      ) : null}
                      <span className="ife-progress-item-title">{item.title}</span>
                      {item.message && (
                        <span className="ife-progress-item-msg">{item.message}</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="ife-footer">
                  <button className="ife-btn ife-btn--primary" onClick={handleClose}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ImportFromExcel;
